import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { PageShell, ShellSection } from "./PageShell";
import { StatusBadge } from "../components/common";
import { Reveal } from "../components/Reveal";
import { PRICING, inquiryMailto } from "../data/content";
import { usePageMeta } from "../hooks/usePageMeta";

const PriceCard = ({ item, i }) => (
    <Reveal delay={i * 0.07} className="bg-thg-surface border border-thg-border p-8 md:p-10 flex flex-col">
        <div className="flex flex-wrap items-center gap-3">
            <StatusBadge>{item.status}</StatusBadge>
        </div>
        <h3 className="mt-5 font-display text-2xl font-medium text-white">{item.name}</h3>
        <p className="mt-1 text-sm text-thg-tertiary">{item.tag}</p>
        <p className="mt-6 font-display text-4xl font-medium text-white">
            {item.priceMain} <span className="text-sm text-thg-tertiary font-sans">{item.priceUnit}</span>
        </p>
        <p className="mt-2 text-sm text-thg-secondary">{item.priceAlt}</p>
        <ul className="mt-6 space-y-3 flex-1">
            {item.points.map((pt) => (
                <li key={pt} className="flex items-start gap-3 text-sm leading-relaxed text-thg-secondary">
                    <span className="mt-2 block w-1.5 h-1.5 bg-thg-red shrink-0" aria-hidden="true" />
                    {pt}
                </li>
            ))}
        </ul>
        <div className="mt-8 flex flex-wrap items-center gap-4">
            <a
                href={inquiryMailto(item.cta.subject)}
                data-testid={`pricing-cta-${i}`}
                className="inline-flex items-center gap-2 bg-thg-red text-white hover:bg-thg-redhover transition-colors duration-300 px-5 py-2.5 text-sm tracking-wide uppercase"
            >
                {item.cta.label} <ArrowUpRight size={15} />
            </a>
            <Link
                to={item.to}
                className="inline-flex items-center gap-1.5 text-sm text-thg-secondary hover:text-white transition-colors duration-300"
            >
                Product page <ArrowRight size={14} />
            </Link>
        </div>
        <p className="mt-4 text-xs text-thg-tertiary">{item.note}</p>
    </Reveal>
);

export default function PricingPage() {
    usePageMeta(
        "Pricing & Engagement | Titan Haven Group LLC",
        "Clear entry points. Scope before surprises. WarRoom StoreCentral, Control Shift, Sarge, the Store Manager Control Kit, and scoped THG service engagements.",
    );
    return (
        <PageShell
            testId="pricing-page"
            overline="Pricing & Engagement"
            title="Clear entry points. Scope before surprises."
            lede="Pricing for THG's commercial software and digital resources — public where self-serve, scoped by written proposal where deployment fit matters — plus honest starting ranges for scoped service engagements. Software subscriptions, digital resources, and scoped services are separate things."
        >
            <ShellSection>
                <Reveal>
                    <h2 className="font-display text-2xl md:text-3xl font-medium text-white">Software subscriptions</h2>
                </Reveal>
                <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-8" data-testid="pricing-software">
                    {PRICING.software.map((item, i) => (
                        <PriceCard key={item.name} item={item} i={i} />
                    ))}
                </div>
            </ShellSection>
            <ShellSection>
                <Reveal>
                    <h2 className="font-display text-2xl md:text-3xl font-medium text-white">Digital resources</h2>
                </Reveal>
                <Reveal delay={0.05} className="mt-10 bg-thg-surface border border-thg-border p-8 md:p-10 max-w-3xl" data-testid="pricing-resource">
                    <h3 className="font-display text-xl font-medium text-white">{PRICING.resources[0].name}</h3>
                    <p className="mt-1 text-sm text-thg-tertiary">{PRICING.resources[0].tag}</p>
                    <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-px bg-thg-border border border-thg-border">
                        {PRICING.resources[0].tiers.map((t) => (
                            <div key={t.name} className="bg-[#0a0a0a] p-5">
                                <p className="text-xs tracking-[0.15em] uppercase text-thg-tertiary">{t.name}</p>
                                <p className="mt-2 font-display text-2xl font-medium text-white">{t.price}</p>
                                {t.note && <p className="mt-1 text-xs text-thg-tertiary">{t.note}</p>}
                            </div>
                        ))}
                    </div>
                    <a
                        href={inquiryMailto(PRICING.resources[0].cta.subject)}
                        data-testid="pricing-resource-cta"
                        className="mt-8 inline-flex items-center gap-2 border border-white/40 text-white hover:bg-white hover:text-black transition-colors duration-300 px-5 py-2.5 text-sm tracking-wide uppercase"
                    >
                        {PRICING.resources[0].cta.label} <ArrowUpRight size={15} />
                    </a>
                </Reveal>
            </ShellSection>
            <ShellSection>
                <Reveal>
                    <h2 className="font-display text-2xl md:text-3xl font-medium text-white">Scoped services</h2>
                    <p className="mt-4 text-sm md:text-base leading-relaxed text-thg-secondary max-w-3xl">
                        Service engagements are scoped, not fixed-scope packages. Ranges below are entry points;
                        a written scope governs the actual engagement.
                    </p>
                </Reveal>
                <div className="mt-10 border-t border-thg-border" data-testid="pricing-services">
                    {PRICING.services.map((s, i) => (
                        <Reveal
                            key={s.name}
                            delay={i * 0.05}
                            className="grid grid-cols-1 md:grid-cols-12 gap-3 py-6 border-b border-thg-border items-baseline"
                        >
                            <h3 className="md:col-span-6 font-display text-lg font-medium text-white">{s.name}</h3>
                            <p className="md:col-span-4 text-sm text-thg-secondary">{s.price}</p>
                            <Link
                                to={s.to}
                                className="md:col-span-2 md:text-right inline-flex md:justify-end items-center gap-1.5 text-xs tracking-[0.15em] uppercase text-thg-secondary hover:text-white transition-colors duration-300"
                            >
                                Details <ArrowRight size={13} />
                            </Link>
                        </Reveal>
                    ))}
                </div>
            </ShellSection>
            <ShellSection>
                <Reveal className="border border-thg-border bg-thg-surface p-8 md:p-10" data-testid="pricing-partners">
                    <h2 className="font-display text-2xl md:text-3xl font-medium text-white">{PRICING.partner.title}</h2>
                    <ul className="mt-6 space-y-3 max-w-3xl">
                        {PRICING.partner.points.map((pt) => (
                            <li key={pt} className="flex items-start gap-3 text-sm leading-relaxed text-thg-secondary">
                                <span className="mt-2 block w-1.5 h-1.5 bg-thg-red shrink-0" aria-hidden="true" />
                                {pt}
                            </li>
                        ))}
                    </ul>
                    <a
                        href={inquiryMailto(PRICING.partner.cta.subject)}
                        data-testid="pricing-partner-cta"
                        className="mt-8 inline-flex items-center gap-2 bg-thg-red text-white hover:bg-thg-redhover transition-colors duration-300 px-5 py-2.5 text-sm tracking-wide uppercase"
                    >
                        {PRICING.partner.cta.label} <ArrowUpRight size={15} />
                    </a>
                </Reveal>
            </ShellSection>
            <ShellSection>
                <Reveal>
                    <ul className="space-y-2 max-w-3xl" data-testid="pricing-notes">
                        {PRICING.notes.map((n) => (
                            <li key={n} className="text-xs leading-relaxed text-thg-tertiary">
                                {n}
                            </li>
                        ))}
                    </ul>
                </Reveal>
            </ShellSection>
        </PageShell>
    );
}
